import { CurrentAccountTypeEnum, FundsRecordAccountTypesOnlyEnum } from '@/enums/businessEnum';
import CacheEnum from '@/enums/cacheEnum';
import PageEnum from '@/enums/pageEnum';
import { useLocalStorage } from '@/hooks/useStorage';
import {
  fiatCurrencyBalanceApi,
  fundingOverviewSocketMapApi,
  getAllVipConfigDataApi,
  getApplyUpgradeVipLevelApi,
  getCurrentUserVipLevelApi,
  getCurrentVipConfigApi,
  getUserAccountTypeApi,
  getUserInformation,
  walletFundApi,
  walletStatisticApi,
} from '@/services/api/requestApi';
import { useReq } from '@/services/net/request';
import { getFingerprint } from '@/utils';
import { history, useModel } from '@umijs/max';
import { useDebounceEffect, usePrevious } from 'ahooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProjectSetting from '@/setting/projectSetting';
import { VerifiedStatesEnum } from '@/pages/mine/index/useStateHooks';
import { calculateProfitLoss } from '@/utils/trade';
import { toJsonData } from '@/utils/socket';

// User接口定义了用户信息的数据结构
export interface User {
  status: number; // 用户状态
  phone: null | string; // 用户电话，可能为null
  walletBalance: number; // 钱包余额
  sysUserId: null | number; // 系统用户ID，可能为null
  memberTime: number; // 会员开始时间
  memberDueDate: number; // 会员到期时间
  totalValue: null | number; // 总资产值，可能为null
  todayProfitAndLoss: null | number; // 今日盈亏，可能为null
  historyProfitAndLoss: null | number; // 历史盈亏，可能为null
  usdExchangeJpy: number; // USD对JPY的汇率
  auditStatus: number; // 审核状态
  socketId: string; // WebSocket的ID
  netWorth: number; // 净值
  occupationMargin: number; // 占用保证金
  availableMargin: number; // 可用保证金
  marginProportion: number; // 保证金比例
  userId: number; // 用户ID
  invitationNo: number; // 邀请码
  avatar: string; // 头像URL
}

// 默认导出的函数初始化并管理用户信息和相关数据
export default function() {
  const { t } = useTranslation();
  // 使用model来管理WebSocket连接
  const { connectWebSocket, onclose, updateUserAmount, setSocketId, sendMsg } = useModel('socket');
  // const { imDisconnect } = useModel('imSocket');
  // 使用local storage来管理用户信息
  const [user, setUser] = useLocalStorage<any>({
    key: CacheEnum.APP_USER,
    defaultValue: {},
  });

  // 账户类型
  const AccountTypesOptions = [
    {
      text: t('{{coin}}账户', {
        coin: ProjectSetting.APP_DEFAULT_CURRENCY,
      }),
      value: FundsRecordAccountTypesOnlyEnum.SAR_ACCOUNT,
    },
    {
      text: t('USD账户'),
      value: FundsRecordAccountTypesOnlyEnum.USD_ACCOUNT,
    },
  ];

  // 账户选择 [0 sar 1 usd]
  const [choiceAccountType, setChoiceAccountType] = useLocalStorage({
    key: 'FundsRecordAccountType',
    defaultValue: FundsRecordAccountTypesOnlyEnum.SAR_ACCOUNT,
  });
  const { getSocketRowByName, lastMessageTime } = useModel('socket');
  //钱包余额 总览
  const { data: walletStatistic = {} as any, runAsync: walletStatisticReq } =
    useReq(() => walletStatisticApi({ noMsg: true }), {
      manual: true,
      cacheKey: CacheEnum.APP_WALLETSTATISTIC,
    });

  //钱包余额 合约
  const { data = {} as any, runAsync: fiatCurrencyBalanceRequest } = useReq(
    () => fiatCurrencyBalanceApi(),
    {
      manual: true,
      cacheKey: CacheEnum.APP_CONTRACTWALLETAPI,
    },
  );

  // 用户账号(0 sar 1 usd) 余额
  const {
    data: getUserAccountType_wallet = {} as any,
    run: getUserAccountTypeReqRun,
    runAsync: getUserAccountTypeReq,
    loading: getUserAccountTypeLoading,
  } = useReq(getUserAccountTypeApi, {
    loadingDefault: false,
    manual: true,
    cacheKey: CacheEnum.APP_AccountTypeWALLET,
  });

  //钱包余额 币币
  const { data: coinsFunds = {} as any, runAsync: walletFundReq } = useReq(
    () => walletFundApi({ noMsg: true }),
    {
      manual: true,
      cacheKey: CacheEnum.APP_WALLETFUNDAPI,
    },
  );
  /*------------------------------------新项目的资金页面所需要的数据-------------------------------------------------------------*/
  // 股票
  const [stockFunds, setStockFunds] = useState<any>();
  // 合约
  const [contractFunds, setContractFunds] = useState<any>();
  // 基金
  const [funds, setFunds] = useState<any>();

  /*------------------------------------新项目的资金页面所需要的数据-------------------------------------------------------------*/
  const [fundingOverviewSocketMapApiData, setFundingOverviewSocketMapApiData] =
    useLocalStorage<any>({
      key: CacheEnum.APP_WALLETFUNDAPI,
      defaultValue: {},
    });

  const { runAsync: fundingOverviewSocketMapReq } = useReq(
    () => fundingOverviewSocketMapApi(),
    {
      manual: true,
      throttleWait: 2000,
      staleTime: 2 * 1000, // 缓存超时时间
      onSuccess(data) {
        // 接口成功返回后，将数据保存到缓存中
        setFundingOverviewSocketMapApiData(data);
      },
    },
  );

  // 资金相关(socket和接口融合-socket优先) 持仓列表中的盈亏自己去订单盈亏里面查询替换（保证最后统计的盈亏和订单是一致的）
  const {
    setFundingOverviewSocketMap,
    fundingOverviewSocketMap,
    getSocketRowByOrderId,
    order,
    refreshWalletKey,
    isSub
  } = useModel('socket') || {};
  // 监听 fundingOverviewSocketMap 的变化，当其有更新时，也更新缓存
  useEffect(() => {
    if (fundingOverviewSocketMap) {
      setFundingOverviewSocketMapApiData((prevData: any) => ({
        ...prevData,
        ...fundingOverviewSocketMap,
      }));
    }
  }, [fundingOverviewSocketMap, setFundingOverviewSocketMapApiData]);
  // 刷新资金 【审核通过刷新钱包】

  useDebounceEffect(
    () => {
      fundingOverviewSocketMapReq();
      // getUserAccountTypeReqRun({ accountType: choiceAccountType, noMsg: true });
    },
    [refreshWalletKey],//updateUserAmount
    {
      wait: 700,
    },
  );

  const renderFunds = useMemo(() => {
    // 如果socket有数据提前返回优先
    return {
      // 总览
      overview: {
        // 总资产
        total:
          fundingOverviewSocketMap?.overview?.ta ??
          fundingOverviewSocketMapApiData?.overview?.ta,
        // 总持仓价值
        totalPositionValue:
          fundingOverviewSocketMap?.overview?.tpv ??
          fundingOverviewSocketMapApiData?.overview?.tpv,
        // 总浮动盈亏
        totalProfitAndLoss:
          fundingOverviewSocketMap?.overview?.tfpl ??
          fundingOverviewSocketMapApiData?.overview?.tfpl,
        // 总可用资产
        totalAvailable:
          fundingOverviewSocketMap?.overview?.taa ??
          fundingOverviewSocketMapApiData?.overview?.taa,
        // 总冻结金额
        totalFrozenAmount:
          fundingOverviewSocketMap?.overview?.tfa ??
          fundingOverviewSocketMapApiData?.overview?.tfa,
      },
      // 合约
      contract: {
        // 合约总资产
        total:
          fundingOverviewSocketMap?.contract?.ta ??
          fundingOverviewSocketMapApiData?.contract?.ta,
        // 合约总持仓价值
        totalPositionValue:
          fundingOverviewSocketMap?.contract?.tpv ??
          fundingOverviewSocketMapApiData?.contract?.tpv,
        // 合约总浮动盈亏
        totalProfitAndLoss:
          fundingOverviewSocketMap?.contract?.tfpl ??
          fundingOverviewSocketMapApiData?.contract?.tfpl,
        // 合约总可用资产
        totalAvailable:
          fundingOverviewSocketMap?.contract?.taa ??
          fundingOverviewSocketMapApiData?.contract?.taa,
        // 合约总冻结金额
        totalFrozenAmount:
          fundingOverviewSocketMap?.contract?.tfa ??
          fundingOverviewSocketMapApiData?.contract?.tfa,
        // 持仓列表
        plList: (
          fundingOverviewSocketMap?.contract?.plList ??
          fundingOverviewSocketMapApiData?.contract?.plList
        )?.map?.((item: any) => {
          const findItem = getSocketRowByOrderId(item?.id);
          if (findItem?.id)
            return {
              id: findItem?.id,
              plAmount: findItem?.plAmount,
              plRatio: findItem?.plRatio,
            };
          return item;
        }),
      },
      // 股票【沙特】
      stock: {
        // 股票总资产
        total:
          fundingOverviewSocketMap?.stock?.ta ??
          fundingOverviewSocketMapApiData?.stock?.ta,
        // 股票总持仓价值
        totalPositionValue:
          fundingOverviewSocketMap?.stock?.tpv ??
          fundingOverviewSocketMapApiData?.stock?.tpv,
        // 股票总浮动盈亏
        totalProfitAndLoss: (
          fundingOverviewSocketMap?.stock?.plList ??
          fundingOverviewSocketMapApiData?.stock?.plList
        )?.reduce(
          (acc: number, cur: any) => {
            const socketData = getSocketRowByName(cur?.subscribeSymbol);
            return (
              acc +
              calculateProfitLoss({
                marketPrice: socketData?.a,
                openPrice: cur?.endPrice,
                leverage: cur?.lever,
                margin: cur?.earnestMoney,
                isLong: cur?.direction === 1, // 是否 开多 direction=1 买多 ，direction=2 买空
              })
            );
          },
          0,
        ),
        // 股票总可用资产
        totalAvailable:
          fundingOverviewSocketMap?.stock?.taa ??
          fundingOverviewSocketMapApiData?.stock?.taa,
        // 股票总冻结金额
        totalFrozenAmount:
          fundingOverviewSocketMap?.stock?.tfa ??
          fundingOverviewSocketMapApiData?.stock?.tfa,
        // 持仓列表
        plList: (
          fundingOverviewSocketMap?.stock?.plList ??
          fundingOverviewSocketMapApiData?.stock?.plList
        )?.map?.((item: any) => {
          const findItem = getSocketRowByOrderId(item?.id);
          if (findItem?.id)
            return {
              id: findItem?.id,
              plAmount: findItem?.plAmount, // 盈亏
              plRatio: findItem?.plRatio, // 盈亏比
            };
          return item;
        }),
      },
      // 股票【美国】
      stockUsd: {
        // 股票总资产
        total:
          fundingOverviewSocketMap?.stockUsd?.ta ??
          fundingOverviewSocketMapApiData?.stockUsd?.ta,
        // 股票总持仓价值
        totalPositionValue:
          fundingOverviewSocketMap?.stockUsd?.tpv ??
          fundingOverviewSocketMapApiData?.stockUsd?.tpv,
        // 股票总浮动盈亏
        totalProfitAndLoss: (
          fundingOverviewSocketMap?.stockUsd?.plList ??
          fundingOverviewSocketMapApiData?.stockUsd?.plList
        )?.reduce(
          (acc: number, cur: any) => {
            const socketData = getSocketRowByName(cur?.subscribeSymbol);
            return (
              acc +
              calculateProfitLoss({
                marketPrice: socketData?.a,
                openPrice: cur?.endPrice,
                leverage: cur?.lever,
                margin: cur?.earnestMoney,
                isLong: cur?.direction === 1, // 是否 开多 direction=1 买多 ，direction=2 买空
              })
            );
          },
          0,
        ),
        // 股票总可用资产
        totalAvailable:
          fundingOverviewSocketMap?.stockUsd?.taa ??
          fundingOverviewSocketMapApiData?.stockUsd?.taa,
        // 股票总冻结金额
        totalFrozenAmount:
          fundingOverviewSocketMap?.stockUsd?.tfa ??
          fundingOverviewSocketMapApiData?.stockUsd?.tfa,
        // 持仓列表
        plList: (
          fundingOverviewSocketMap?.stockUsd?.plList ??
          fundingOverviewSocketMapApiData?.stockUsd?.plList
        )?.map?.((item: any) => {
          const findItem = getSocketRowByOrderId(item?.id);
          if (findItem?.id)
            return {
              id: findItem?.id,
              plAmount: findItem?.plAmount, // 盈亏
              plRatio: findItem?.plRatio, // 盈亏比
            };
          return item;
        }),
      },
      // 汇率
      exchangeRate:
        fundingOverviewSocketMap?.exchangeRate ??
        fundingOverviewSocketMapApiData?.exchangeRate,
    };
  }, [
    fundingOverviewSocketMap,
    fundingOverviewSocketMapApiData,
    order,
    location.pathname,
    lastMessageTime,
  ]);

  // 刷新饼图
  const [pieKey, setPieKey] = useState(1);
  // 获取饼图数据

  // 计算股票总浮动盈亏
  const stockProfitAndLoss = useMemo(() => {
    // 根据plAmount计算综合
    return renderFunds?.stock?.plList?.reduce((acc: number, cur: any) => {
      return acc + (cur?.plAmount ?? 0);
    }, 0);
  }, [renderFunds]);

  // 计算合约总浮动盈亏
  const contractProfitAndLoss = useMemo(() => {
    // 根据plAmount计算综合
    return renderFunds?.contract?.plList?.reduce((acc: number, cur: any) => {
      return acc + (cur?.plAmount ?? 0);
    }, 0);
  }, [renderFunds]);
  // 计算总览盈亏
  const overviewProfitAndLoss = useMemo(() => {
    return (
      stockProfitAndLoss +
      contractProfitAndLoss * (renderFunds?.exchangeRate ?? 0)
    );
  }, [stockProfitAndLoss, contractProfitAndLoss, renderFunds, order]);
  // 获取股票订单盈亏(订单调用)
  const getStockProfitAndLoss = useCallback(
    (id: string) => {
      const findItem = renderFunds?.stock?.plList?.find(
        (item: any) => item?.id === id,
      );
      return findItem ?? {};
    },
    [renderFunds, order],
  );
  // 获取合约订单盈亏(订单调用)
  const getContractProfitAndLoss = useCallback(
    (id: string) => {
      const findItem = renderFunds?.contract?.plList?.find(
        (item: any) => item?.id === id,
      );
      return findItem ?? {};
    },
    [renderFunds, order],
  );

  // 刷新钱包方法
  const refreshWallet = async () => {
    fundingOverviewSocketMapReq();
    // getUserAccountTypeReqRun({ accountType: choiceAccountType, noMsg: true });
  };

  // 当前持仓所有符号(再根据字母排序)
  const allSymbol = useMemo(() => {
    return JSON.stringify([
      ...(renderFunds?.stock?.plList ?? []),
      ...(renderFunds?.contract?.plList ?? []),
      ...(renderFunds?.stockUsd?.plList ?? []),
    ]?.filter(item => !!item)?.map?.((item: any) => item?.subscribeSymbol)?.sort?.((a, b) => {
      return a?.localeCompare(b);
    }));
  }, [renderFunds]);
  useEffect(() => {
    if(toJsonData(allSymbol) !== "[]"){
      sendMsg(
        `{"type":"SUB","data": ${allSymbol}}`,
      );
    }
  }, [allSymbol,isSub]);

  function insertCustomerServiceScript() {
    // 检查是否已经有了客服脚本
    if (!document.getElementById('customerServiceScript')) {
      const script = document.createElement('script');
      script.src =
        'https://assets.salesmartly.com/js/project_138030_143253_1727794591.js';
      script.id = 'customerServiceScript';
      document.body.appendChild(script);
    }
  }

  function removeCustomerServiceScript() {
    const script = document.getElementById('customerServiceScript');
    if (script) {
      script.remove(); // 移除DOM中的客服脚本
    }
  }

  //登录提交
  const [token] = useLocalStorage({
    key: CacheEnum.AUTHORIZATION,
  });

  // 获取VIP配置
  const {
    data: vipConfig,
    runAsync: getVipConfig,
    loading: vipConfigLoading,
  } = useReq<any[], any>(getAllVipConfigDataApi, {
    manual: true,
  });

  // 获取用户的 vip 等级可升级的信息
  const {
    data: userVipInfo = {} as any,
    runAsync: getUserVipInfo,
    loading: userVipInfoLoading,
    mutate: setUserVipInfo,
  } = useReq(getCurrentUserVipLevelApi, {
    manual: true,
  });

  // 获取用户的 vip 等级
  const {
    data: applyUpgradeVipLevelInfo = {} as any,
    runAsync: getApplyUpgradeVipLevel,
    loading: applyUpgradeVipLevelLoading,
  } = useReq(getApplyUpgradeVipLevelApi, {
    manual: true,
  });
  // 当前会员vip配置信息
  const {
    run: getCurrentVipConfigReq,
    data: currentVipConfig,
    loading: currentVipConfigLoading,
  } = useReq(getCurrentVipConfigApi, {
    manual: true,
  });


  //用户信息
  const { runAsync: requestUserInfo } = useReq(
    () => {
      // 在登录或注册页面时不请求用户信息
      // get-current-member
      // if (['/login', '/register'].includes(window.location.pathname))
      //   return null;
      return getUserInformation();
    },
    {
      manual:
        window.location.pathname === '/register' ||
        window.location.pathname === '/login' ||
        window.location.pathname === '/forgot',
      staleTime: 1 * 1000, // 缓存超时时间
      formatResult: (res: any) => res, // 结果格式化函数
      onSuccess: async (res: any) => {
        const data = res?.data ?? {};
        getVipConfig();
        getCurrentVipConfigReq();
        if (data) {
          refreshWallet();
          // 基金资金
          // setFunds(
          //   (await getFundRecordsMoneyReq({
          //     walletType: WalletTypeEnum.FUND,
          //   })) as any,
          // );
          // refreshWallet();
          setUser(data);
          fiatCurrencyBalanceRequest();
          // getUserAccountTypeReq({
          //   accountType: choiceAccountType,
          //   noMsg: true,
          // });
          walletFundReq();
          walletStatisticReq();
          const localStorageToken = localStorage.getItem(
            CacheEnum.AUTHORIZATION,
          );

          // insertCustomerServiceScript();

          if (localStorageToken) {
            const localToken = JSON.parse(
              localStorage.getItem(CacheEnum.AUTHORIZATION) as string,
            );
            connectWebSocket(localToken);
            // imDisconnect();
          }
        }
      },
      onError(res: any) {
        // 处理401错误，清除授权信息
        if (res?.code === 401) {
          localStorage.removeItem(CacheEnum.AUTHORIZATION);
          return;
        }
      },
    },
  );

  // 在令牌变更时尝试使用令牌重新连接WebSocket
  useEffect(() => {
    if (token) {
      connectWebSocket(token);
    }
  }, [token]);
  // 使用model来管理Socket连接中的用户金额信息
  const { userSoketAmount, fundingOverviewSocket = {} } = useModel('socket');

  // 合约金额
  const userAmount: any = useMemo(() => {
    let res = data;
    // 如果有来自Socket的用户金额信息，则更新余额字段
    if (userSoketAmount) {
      res = {
        available: userSoketAmount.available, // 可用余额
        freeze: userSoketAmount.freeze, // 冻结金额
        historyPlAmount: data.historyPlAmount, // 历史盈亏
        plAmount: userSoketAmount.plAmount, // 未实现盈亏
        total: userSoketAmount.walletBalance, // 总资产
        walletBalance: userSoketAmount.walletBalance, // 余额
        earnestMoney: userSoketAmount.earnestMoney, // 余额
        unrealizedProfitLoss: userSoketAmount.unrealizedProfitLoss, // 余额
      };
    }
    return res;
  }, [userSoketAmount, data]);

  // 总览金额
  const userOverview: any = useMemo(() => {
    return {
      totalAmount: fundingOverviewSocket.totalAmount ?? walletStatistic?.total, // 可用余额
      dayPl: walletStatistic.dayPl, // 今日盈亏
      monthPl: walletStatistic.monthPl, // 历史盈亏
      ...walletStatistic,
    };
  }, [fundingOverviewSocket, walletStatistic]);

  // useEffect(() => {
  //   getUserAccountTypeReqRun({ accountType: choiceAccountType, noMsg: true });
  // }, [choiceAccountType]);

  // 账户金额
  const accountAmount: any = useMemo(() => {
    return getUserAccountType_wallet;
  }, [getUserAccountType_wallet]);

  // 退出登录
  const logOut = () => {
    // 点击退出登录按钮时，清除sessionStorage和localStorage，并跳转到登录页。
    try {
      console.log('退出登录，执行');
      setFundingOverviewSocketMap({})
      removeCustomerServiceScript();

      sessionStorage.clear();
      const i18nextLng = localStorage.getItem('i18nextLng');
      const nextTheme = localStorage.getItem('nextTheme');
      const LANGUAGE = localStorage.getItem('LANGUAGE');
      localStorage.clear();
      localStorage.setItem('i18nextLng', i18nextLng as any);
      localStorage.setItem('nextTheme', nextTheme as any);
      localStorage.setItem('LANGUAGE', LANGUAGE as any);

      // 关闭WebSocket连接
      onclose();
      history.replace(PageEnum.LOGIN);
      // location.reload();
    } finally {
      // console.log('退出登录 执行报错');
    }
  };

  // url pathname
  const pathname = window.location.pathname;
  const prePathname = usePrevious(pathname);

  // useEffect(() => {
  //   // 监听路由，在某些情况，比如 token过期导致的 退出登录，需要断开soket
  //   if (
  //     prePathname !== undefined &&
  //     prePathname !== PageEnum.LOGIN &&
  //     pathname === PageEnum.LOGIN
  //   ) {
  //     logOut();
  //   }
  // }, [pathname]);

  async function setNoLoginInfo() {
    const serviceId = await getFingerprint(); // 同步方式获取到唯一设备码

    window.ssq.push('setLoginInfo', {
      user_id: serviceId, // 此次对话的唯一标识, 随机生成, 必填
      user_name: '未登录用户' + serviceId, // 客服后台显示的用户名, 如果已经实名认证则传真实姓名, 没有的话传注册邮箱或手机号, 必填
      phone: user?.phone, // 注册手机号, 选填
      email: user?.email, // 注册邮箱, 选填
    });
  }

  async function setLoginInfo() {
    const uniId = user?.serviceId || user?.id;

    window.ssq.push('setLoginInfo', {
      user_id: uniId + 'awusuaq2', // 此次对话的唯一标识, 随机生成, 必填
      user_name: user?.realName ?? user?.phone ?? user?.name ?? user?.email, // 客服后台显示的用户名, 如果已经实名认证则传真实姓名, 没有的话传注册邮箱或手机号, 必填
      phone: user?.phone, // 注册手机号, 选填
      email: user?.email, // 注册邮箱, 选填
    });
  }

  // useEffect(() => {
  //     if (window.ssq) {
  //         if (user?.id || user?.serviceId) {
  //             setLoginInfo();
  //         } else {
  //             setNoLoginInfo();
  //         }
  //     }
  // }, [window.ssq, user]);

  const isRealname: boolean = useMemo(() => {
    // 返回 用户 是否已经 实名
    if (user) {
      return user?.realnameStatus === VerifiedStatesEnum.REAL;
    }
    return false;
  }, [user?.realnameStatus]);
  /**
   * @description 跳转客服
   * @param data 订单详情数据
   * @param typeCode 类型键值
   */
  const gotoCustomerService = () => {
    // 跳转在线客服页面
    history.push(PageEnum.ONLINESUPPORT);

  };


  // 返回管理的用户信息、请求用户信息函数和用户金额信息
  return {
    isRealname,
    user,
    getUserAccountTypeLoading,
    accountAmount,
    AccountTypesOptions,
    choiceAccountType,
    setChoiceAccountType,
    requestUserInfo,
    userAmount,
    logOut,
    fiatCurrencyBalanceRequest,
    userOverview,
    walletStatisticReq,
    coinsFunds,
    walletFundReq,
    refreshWallet,
    stockFunds,
    contractFunds,
    funds,
    setUser,
    setStockFunds,
    renderFunds,
    stockProfitAndLoss,
    contractProfitAndLoss,
    overviewProfitAndLoss,
    getStockProfitAndLoss,
    getContractProfitAndLoss,
    pieKey,
    applyUpgradeVipLevelLoading,
    getApplyUpgradeVipLevel,
    applyUpgradeVipLevelInfo,
    userVipInfo,
    getUserVipInfo,
    setUserVipInfo,
    vipConfig,
    getVipConfig,
    currentVipConfig,
    currentVipConfigLoading,
    vipLoading: userVipInfoLoading || vipConfigLoading,
    gotoCustomerService,

  };
}
